import { useState, useEffect } from 'react';
import { apiFetch } from '../../utils/apiFetch';
import { getAuthToken } from '../../utils/authStorage';

/**
 * useMarkupSymbols - Shared symbol/stamp library state hook
 *
 * Extracts all symbol-related state so that both PDFViewerArea's
 * useMarkups and InfiniteView's useInfiniteMarkups share the same
 * symbol library, search, view mode, and default signature.
 *
 * Symbol state is global (both views share the same localStorage keys):
 *   - 'markup_symbols'          — saved symbols array
 *   - 'symbols_view_mode'       — grid / list
 *   - 'markup_default_signature' — ID of the default signature
 *
 * No parameters needed.
 *
 * @returns Symbol state + setters
 */
/** Fired after any write to the symbol store, so open panels re-read it. */
export const SYMBOLS_CHANGED_EVENT = 'pidly:symbols-changed';

// ── The store lives in IndexedDB, mirrored in memory ─────────────────────
//
// It lived in ONE localStorage key, which has a ~5MB ceiling — a few
// batches of proper-quality stamps filled it, the write threw, and "Add"
// reported the store full. Worse, the key was in the account-preferences
// sync list, pushing megabytes of PNGs through a preferences endpoint on
// every change. IndexedDB has no practical ceiling for this; the in-memory
// mirror keeps every existing synchronous reader working exactly as
// before, and hydration fires the changed event so mounted panels re-read.
//
// One-time migration: existing localStorage symbols seed IndexedDB, then
// the localStorage key is removed — freeing the quota for everything else.
const IDB_NAME = 'pidly_symbol_store';
const IDB_STORE = 'kv';
const IDB_KEY = 'markup_symbols';

function idbOpen() {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(IDB_NAME, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(IDB_STORE);
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}
async function idbGet() {
  const db = await idbOpen();
  return new Promise((resolve, reject) => {
    const t = db.transaction(IDB_STORE, 'readonly').objectStore(IDB_STORE).get(IDB_KEY);
    t.onsuccess = () => resolve(t.result || null);
    t.onerror = () => reject(t.error);
  });
}
async function idbSet(value) {
  const db = await idbOpen();
  return new Promise((resolve, reject) => {
    const t = db.transaction(IDB_STORE, 'readwrite');
    t.objectStore(IDB_STORE).put(value, IDB_KEY);
    t.oncomplete = () => resolve();
    t.onerror = () => reject(t.error);
  });
}

function readLocalStorageStore() {
  try {
    const saved = localStorage.getItem('markup_symbols');
    const arr = saved ? JSON.parse(saved) : [];
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

let memStore = null;   // null until hydrated; readers fall back to localStorage
(function hydrateSymbolStore() {
  if (typeof indexedDB === 'undefined') { memStore = readLocalStorageStore(); return; }
  idbGet().then(async (fromIdb) => {
    const fromLs = readLocalStorageStore();
    if (Array.isArray(fromIdb) && fromIdb.length) {
      memStore = fromIdb;
    } else if (fromLs.length) {
      memStore = fromLs;
      try {
        await idbSet(fromLs);
        localStorage.removeItem('markup_symbols');
      } catch { /* keep localStorage as the copy */ }
    } else {
      memStore = [];
    }
    notifySymbolsChanged();
    syncFromServer();
  }).catch(() => {
    memStore = readLocalStorageStore();
    notifySymbolsChanged();
    syncFromServer();
  });
})();

// ── Account-level roaming ────────────────────────────────────────────────
// The library also lives on the backend (per account, its own column and
// endpoint — see /api/me/symbols), so stamps follow the user between
// browsers and machines. The SERVER copy is canonical on load; a machine
// whose server copy is empty seeds it from local (the migration path).
// Saves push debounced, whole-array, last write wins.
async function syncFromServer() {
  // Not signed in yet (the module-load pull runs on the /login page too):
  // a 401 here used to be swallowed and the library was never pulled for
  // the whole session — stamps only appeared after a hard reload. The
  // preferences hydrate re-runs this once the user is in.
  if (!getAuthToken()) return;
  try {
    const r = await apiFetch('/api/me/symbols');
    if (!r.ok) return;
    const j = await r.json();
    const server = Array.isArray(j?.symbols) ? j.symbols : [];
    if (server.length) {
      memStore = server;
      if (typeof indexedDB !== 'undefined') idbSet(server).catch(() => {});
      notifySymbolsChanged();
    } else if ((memStore || []).length) {
      pushToServer(memStore);
    }
  } catch { /* offline — local copy serves */ }
}

let pushTimer = null;
function pushToServer(symbols) {
  if (pushTimer) clearTimeout(pushTimer);
  pushTimer = setTimeout(async () => {
    pushTimer = null;
    try {
      const r = await apiFetch('/api/me/symbols', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbols }),
      });
      if (r.status === 409) {
        // Your toolbox is full (500 items, signatures not counted: backend
        // utils/toolboxRules). Said, not just logged: the new item would
        // otherwise vanish at the next sign-in.
        const body = await r.json().catch(() => ({}));
        try { (await import('../../utils/notices')).showNotice({ kind: 'error', title: 'Your toolbox is full', detail: body.error || 'Delete some items to add more.' }); } catch { /* no notices */ }
      } else if (!r.ok) console.warn('[symbols] server sync failed:', r.status);
    } catch (e) {
      console.warn('[symbols] server sync failed:', e?.message || e);
    }
  }, 2500);
}

/** Pull the account's library now. Called by preferencesSync once the
 *  user is signed in; safe to call any time (server copy wins if non-empty). */
export function syncSymbolsFromServer() { return syncFromServer(); }

export function readSymbolStore() { return readStore(); }

function readStore() {
  return memStore != null ? memStore : readLocalStorageStore();
}

/**
 * Write the store AND tell everyone.
 *
 * Anything that persists symbols should go through here rather than calling
 * localStorage directly — a write that skips the notification leaves every
 * open toolbox stale, which is invisible until someone wonders where their
 * stamps went.
 */
export function saveSymbolStore(symbols) {
  memStore = Array.isArray(symbols) ? symbols : [];
  if (typeof indexedDB !== 'undefined') {
    idbSet(memStore).catch(e => console.warn('[symbols] IndexedDB write failed:', e?.message || e));
  } else {
    try { localStorage.setItem('markup_symbols', JSON.stringify(memStore)); } catch { /* no store available */ }
  }
  pushToServer(memStore);
  notifySymbolsChanged();
  return memStore;
}

export function notifySymbolsChanged() {
  try { window.dispatchEvent(new Event(SYMBOLS_CHANGED_EVENT)); } catch { /* no-op */ }
}

export default function useMarkupSymbols() {
  // ═══════════════════════════════════════════════════════════════════════════
  // SAVED SYMBOLS — loaded from localStorage('markup_symbols')
  // ═══════════════════════════════════════════════════════════════════════════

  const [savedSymbols, setSavedSymbols] = useState(() => readStore());

  // Live refresh.
  //
  // The store was read once at mount, so anything writing localStorage from
  // outside this hook — symbol extraction adding a batch, another tab, another
  // panel instance — left the toolbox showing a stale list until a reload.
  //
  // Listens for both the in-page event and the cross-tab `storage` event: the
  // browser fires `storage` only in OTHER tabs, so a same-tab write needs its
  // own signal. Writers call notifySymbolsChanged.
  useEffect(() => {
    const reload = () => setSavedSymbols(readStore());
    const onStorage = (e) => { if (!e.key || e.key === 'markup_symbols') reload(); };
    window.addEventListener(SYMBOLS_CHANGED_EVENT, reload);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(SYMBOLS_CHANGED_EVENT, reload);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // SYMBOL DIALOG / CREATION STATE
  // ═══════════════════════════════════════════════════════════════════════════

  const [showSaveSymbolDialog, setShowSaveSymbolDialog] = useState(false);
  const [symbolNameInput, setSymbolNameInput] = useState('');
  const [draggingSymbol, setDraggingSymbol] = useState(null);
  const [symbolCreationMode, setSymbolCreationMode] = useState(false);
  const [symbolCaptureMode, setSymbolCaptureMode] = useState(false);
  const [captureRegion, setCaptureRegion] = useState(null);

  // ═══════════════════════════════════════════════════════════════════════════
  // SEARCH & VIEW MODE
  // ═══════════════════════════════════════════════════════════════════════════

  const [symbolSearchQuery, setSymbolSearchQuery] = useState('');
  const [symbolsViewMode, setSymbolsViewMode] = useState(() => {
    try {
      return localStorage.getItem('symbols_view_mode') || 'grid';
    } catch { return 'grid'; }
  });

  // Persist symbolsViewMode to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('symbols_view_mode', symbolsViewMode);
    } catch (e) { /* ignore */ }
  }, [symbolsViewMode]);

  // ═══════════════════════════════════════════════════════════════════════════
  // DEFAULT SIGNATURE
  // ═══════════════════════════════════════════════════════════════════════════

  const [defaultSignatureId, setDefaultSignatureId] = useState(() => {
    try {
      return localStorage.getItem('markup_default_signature') || null;
    } catch { return null; }
  });

  // Persist default signature ID
  useEffect(() => {
    try {
      if (defaultSignatureId) {
        localStorage.setItem('markup_default_signature', defaultSignatureId);
      } else {
        localStorage.removeItem('markup_default_signature');
      }
    } catch (e) { /* ignore */ }
  }, [defaultSignatureId]);

  // ═══════════════════════════════════════════════════════════════════════════
  // PENDING PLACEMENT — symbol/signature waiting to be rubber-band drawn
  // Shape: { symbol: <symbol object>, isSignature: bool } or null
  // ═══════════════════════════════════════════════════════════════════════════

  const [pendingPlacement, setPendingPlacement] = useState(null);

  // ═══════════════════════════════════════════════════════════════════════════
  // RETURN ALL SYMBOL STATE
  // ═══════════════════════════════════════════════════════════════════════════

  return {
    savedSymbols, setSavedSymbols,
    showSaveSymbolDialog, setShowSaveSymbolDialog,
    symbolNameInput, setSymbolNameInput,
    draggingSymbol, setDraggingSymbol,
    symbolCreationMode, setSymbolCreationMode,
    symbolCaptureMode, setSymbolCaptureMode,
    captureRegion, setCaptureRegion,
    symbolSearchQuery, setSymbolSearchQuery,
    symbolsViewMode, setSymbolsViewMode,
    defaultSignatureId, setDefaultSignatureId,
    pendingPlacement, setPendingPlacement,
  };
}
